import { store } from '../api/client';
import type { components } from '../api/store';
import { problemOf } from './answer';

/** One job of the till's print queue, as the order store answers it. */
export type PrintJob = components['schemas']['PrintJobBody'];

/** A staff decision on a held job (#515). */
export type Decision = components['schemas']['StorePrintDecisionBody']['decision'];

/**
 * Whether the store holds this job for a staff decision (#515): its delivery
 * is uncertain, so it ended failed, and nobody has decided it yet. The store
 * lists such a job whatever its age, until someone decides.
 */
export function isHeld(job: PrintJob): boolean {
	return job.delivery === 'uncertain' && job.decision === undefined;
}

/**
 * The choices a held job offers now, in the order the screen shows them, as
 * the store lists them by the core's rules and its own clock: "I have it"
 * always, another copy of paper (a bill's only while its order is open at
 * the bill's total), and a new kick only within the kick's 2 minutes and
 * when it was not retried. The screen never times or judges a choice itself.
 */
export function heldChoices(job: PrintJob): Decision[] {
	if (!isHeld(job)) return [];
	return job.choices ?? ['confirm_received'];
}

/** What the store said to a decision: the held job with its decision, or the refusal's code. */
export type DecideOutcome = { kind: 'decided'; job: PrintJob } | { kind: 'refused'; code: string };

// One key per held job and decision. A lost answer repeats with the same key,
// so the store answers it as the first rather than deciding twice; an answered
// request makes the next deliberate tap a new key.
const decisionKeys = new Map<string, string>();

/**
 * Decide a held job (#515). Returns the decided job, whose decision names the
 * copy or the kick it made, the refusal's code, or null when the store did
 * not answer.
 */
export async function decidePrint(
	jobId: string,
	decision: Decision,
): Promise<DecideOutcome | null> {
	const name = `${jobId}:${decision}`;
	let key = decisionKeys.get(name);
	if (!key) {
		key = crypto.randomUUID();
		decisionKeys.set(name, key);
	}
	try {
		const { data, error, response } = await store.POST('/store/print-jobs/{jobId}/decision', {
			params: { path: { jobId }, header: { 'Idempotency-Key': key } },
			body: { decision },
		});
		if (response.status < 500) decisionKeys.delete(name);
		if (error === undefined && data) return { kind: 'decided', job: data };
		return { kind: 'refused', code: problemOf(error)?.code ?? String(response.status) };
	} catch {
		return null;
	}
}

/** Keep a second tap from queuing another copy. */
export const PRINT_COOLDOWN_MS = 1000;
const POLL_MS = 1000;
const WAIT_MS = 65_000;

// A lost answer repeats with the same key; an answered request makes the next
// deliberate tap a new bill copy.
const billKeys = new Map<string, string>();

export async function queueBill(orderId: string): Promise<PrintJob | null> {
	let key = billKeys.get(orderId);
	if (!key) {
		key = crypto.randomUUID();
		billKeys.set(orderId, key);
	}
	try {
		const { data, error, response } = await store.POST('/store/orders/{orderId}/bill', {
			params: { path: { orderId }, header: { 'Idempotency-Key': key } },
		});
		if (response.status < 500) billKeys.delete(orderId);
		return error === undefined && data ? data : null;
	} catch {
		return null;
	}
}

/** Wait for the bill job to leave the queue so the screen can offer a retry on failure. */
export async function waitForBill(orderId: string, jobId: string): Promise<boolean> {
	const deadline = Date.now() + WAIT_MS;
	while (Date.now() < deadline) {
		try {
			const { data, error } = await store.GET('/store/print-jobs');
			if (error === undefined && data) {
				const jobs = data.jobs.filter((job) => job.document === 'bill' && job.orderId === orderId);
				const job = jobs.find((candidate) => candidate.id === jobId);
				if (job?.state === 'failed') return false;
				if (!job || job.state === 'printed') return true;
			}
		} catch {
			// Keep watching through temporary store read failures.
		}
		await new Promise((resolve) => setTimeout(resolve, POLL_MS));
	}
	return false;
}

/** An explicit Print again/Reprint, or Orders' test slip. Payment queues its own. */
export async function queuePrint(orderId: string | null): Promise<PrintJob | null> {
	try {
		const { data, error } = await store.POST('/store/print-jobs', {
			body: orderId === null ? { document: 'test' } : { document: 'receipt', orderId },
			params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
		});
		return error === undefined && data ? data : null;
	} catch {
		return null;
	}
}

/** A receipt the store holds for a decision (#515), by its job's id. */
export interface HeldReceipt {
	kind: 'uncertain';
	jobId: string;
}

export type ReceiptResult = 'printed' | 'failed' | 'stopped' | HeldReceipt;

/**
 * Done watches the receipt the payment queued. Printed jobs leave the list.
 * An explicit reprint watches its job id so an older failed copy cannot end it.
 * A receipt the store holds because its delivery is uncertain ends the watch
 * as its own result, with its id, so Done can offer another copy of it.
 * Bound even a lost store call to 65 seconds; closing Done cancels the wait.
 */
export async function waitForReceipt(
	orderId: string,
	signal: AbortSignal,
	jobId?: string,
): Promise<ReceiptResult> {
	if (signal.aborted) return 'stopped';
	let stopped = false;
	let pollTimer: ReturnType<typeof setTimeout> | undefined;
	let wake: (() => void) | undefined;
	let finish!: (result: ReceiptResult) => void;
	const ended = new Promise<ReceiptResult>((resolve) => (finish = resolve));
	const abort = () => finish('stopped');
	signal.addEventListener('abort', abort, { once: true });
	const deadline = setTimeout(() => finish('failed'), WAIT_MS);
	async function poll(): Promise<ReceiptResult> {
		while (!stopped) {
			try {
				const { data, error } = await store.GET('/store/print-jobs');
				if (stopped) return 'stopped';
				if (error === undefined && data) {
					const jobs = data.jobs.filter(
						(job) =>
							job.document === 'receipt' &&
							job.orderId === orderId &&
							(jobId === undefined || job.id === jobId),
					);
					if (!jobs.some((job) => job.state === 'queued' || job.state === 'printing')) {
						const held = jobs.find(isHeld);
						if (held) return { kind: 'uncertain', jobId: held.id };
						return jobs.some((job) => job.state === 'failed') ? 'failed' : 'printed';
					}
				}
			} catch {
				// No answer says nothing about the paper: keep watching until the deadline.
			}
			if (stopped) return 'stopped';
			await new Promise<void>((resolve) => {
				wake = resolve;
				pollTimer = setTimeout(resolve, POLL_MS);
			});
		}
		return 'stopped';
	}
	try {
		return await Promise.race([poll(), ended]);
	} finally {
		stopped = true;
		clearTimeout(deadline);
		clearTimeout(pollTimer);
		wake?.();
		signal.removeEventListener('abort', abort);
	}
}
